import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Chip,
  Avatar,
  Divider,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import { AgGridReact } from "ag-grid-react";
import { useRef } from "react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import axiosInstance from "API/axiosInstance";
import { API_ENDPOINTS } from "config/api";
import GroupWorkIcon from "@mui/icons-material/GroupWork";
import PeopleIcon from "@mui/icons-material/People";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import DescriptionIcon from "@mui/icons-material/Description";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import { getUploadUrl } from "config/api";

const AdminChapterReportDialog = ({ open, onClose, showAllChapters = false }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [chapters, setChapters] = useState([]);
  const [selectedChapter, setSelectedChapter] = useState(null);
  const [chapterMembers, setChapterMembers] = useState([]);
  const [chapterRoles, setChapterRoles] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [viewAllMembers, setViewAllMembers] = useState(showAllChapters);
  const gridRef = useRef(null);

  const getMemberColumnDefs = (showChapterName = false) => [
    {
      headerName: "Photo",
      field: "img",
      width: 90,
      cellRenderer: (params) => {
        const img = params.data?.img || params.data?.Img || "";
        const fullName = params.data?.fullName || params.data?.FullName || "";
        return (
          <Avatar
            src={img ? getUploadUrl(img) : undefined}
            alt={fullName}
            sx={{ width: 32, height: 32, fontSize: 13, margin: "4px auto" }}
          >
            {fullName?.charAt(0)?.toUpperCase() || "?"}
          </Avatar>
        );
      },
      sortable: false,
      filter: false,
      floatingFilter: false,
      suppressSizeToFit: true,
    },
    ...(showChapterName ? [{
      headerName: "Chapter",
      field: "chapterName",
      valueGetter: (p) => p.data?.chapterName || p.data?.ChapterName || "N/A",
      minWidth: 150,
      flex: 1,
      filter: "agTextColumnFilter",
      filterParams: {
        filterOptions: ["contains", "equals", "startsWith", "endsWith"],
        defaultOption: "contains",
      },
    }] : []),
    {
      headerName: "Name",
      field: "fullName",
      valueGetter: (p) => p.data?.fullName || p.data?.FullName || "",
      minWidth: 140,
      flex: 1,
      filter: "agTextColumnFilter",
      filterParams: {
        filterOptions: ["contains", "equals", "startsWith", "endsWith"],
        defaultOption: "contains",
      },
    },
    {
      headerName: "Batch",
      field: "customBatch",
      valueGetter: (p) => p.data?.customBatch || p.data?.CustomBatch || "N/A",
      minWidth: 110,
      filter: "agTextColumnFilter",
      filterParams: {
        filterOptions: ["contains", "equals"],
        defaultOption: "contains",
      },
    },
    {
      headerName: "Role",
      field: "roleName",
      valueGetter: (p) =>
        p.data?.roleName || p.data?.RoleName || p.data?.role || p.data?.Role || "N/A",
      minWidth: 120,
      filter: "agTextColumnFilter",
      filterParams: {
        filterOptions: ["contains", "equals", "startsWith", "endsWith"],
        defaultOption: "contains",
      },
    },
    {
      headerName: "Coordinator",
      field: "isCoordinator",
      valueGetter: (p) => {
        const value = p.data?.isCoordinator !== undefined
          ? p.data.isCoordinator
          : p.data?.IsCoordinator !== undefined
          ? p.data.IsCoordinator
          : false;
        return value ? "Yes" : "No";
      },
      minWidth: 120,
      cellRenderer: (p) => (p.value || "No"),
      filter: "agTextColumnFilter",
      filterParams: {
        filterOptions: ["equals"],
        defaultOption: "equals",
      },
    },
    {
      headerName: "Status",
      field: "isActive",
      valueGetter: (p) => {
        const value = p.data?.isActive !== undefined
          ? p.data.isActive
          : p.data?.IsActive !== undefined
          ? p.data.IsActive
          : true;
        return value ? "Active" : "Inactive";
      },
      minWidth: 110,
      cellRenderer: (p) => {
        const isActive = p.value === "Active";
        return (
          <Box display="flex" alignItems="center" justifyContent="center" gap={0.5}>
            {isActive ? (
              <CheckCircleIcon sx={{ color: "#4caf50", fontSize: 20 }} />
            ) : (
              <CancelIcon sx={{ color: "#f44336", fontSize: 20 }} />
            )}
            <Typography variant="body2" sx={{ fontSize: "0.75rem" }}>
              {p.value || "Inactive"}
            </Typography>
          </Box>
        );
      },
      filter: "agTextColumnFilter",
      filterParams: {
        filterOptions: ["equals"],
        defaultOption: "equals",
      },
    },
    {
      headerName: "Joined Date",
      field: "joinedDate",
      valueGetter: (p) => {
        const date = p.data?.joinedDate || p.data?.JoinedDate || "";
        return date ? new Date(date).toLocaleDateString() : "N/A";
      },
      minWidth: 130,
      cellRenderer: (p) => (p.value || "N/A"),
      filter: "agTextColumnFilter",
      filterParams: {
        filterOptions: ["contains", "equals", "startsWith", "endsWith"],
        defaultOption: "contains",
      },
    },
  ];

  const roleColumnDefs = [
    {
      headerName: "Photo",
      field: "img",
      width: 90,
      cellRenderer: (params) => {
        const img = params.data?.img || params.data?.Img || "";
        const fullName = params.data?.fn || params.data?.FullName || "";
        return (
          <Avatar
            src={img ? getUploadUrl(img) : undefined}
            alt={fullName}
            sx={{ width: 32, height: 32, fontSize: 13, margin: "4px auto" }}
          >
            {fullName?.charAt(0)?.toUpperCase() || "?"}
          </Avatar>
        );
      },
      sortable: false,
      filter: false,
      floatingFilter: false,
      suppressSizeToFit: true,
    },
    {
      headerName: "Name",
      field: "fn",
      valueGetter: (p) => p.data?.fn || p.data?.fullName || p.data?.FullName || "",
      minWidth: 140,
      flex: 1,
      filter: "agTextColumnFilter",
      filterParams: {
        filterOptions: ["contains", "equals", "startsWith", "endsWith"],
        defaultOption: "contains",
      },
    },
    {
      headerName: "Chapter",
      field: "cfn",
      valueGetter: (p) =>
        `${p.data?.cfn || ""}${p.data?.csn ? ` (${p.data.csn})` : ""}`,
      minWidth: 160,
      flex: 1,
      filter: "agTextColumnFilter",
      filterParams: {
        filterOptions: ["contains", "equals", "startsWith", "endsWith"],
        defaultOption: "contains",
      },
    },
    {
      headerName: "Role",
      field: "rfn",
      valueGetter: (p) => p.data?.rfn || p.data?.roleName || p.data?.RoleName || "N/A",
      minWidth: 120,
      filter: "agTextColumnFilter",
      filterParams: {
        filterOptions: ["contains", "equals", "startsWith", "endsWith"],
        defaultOption: "contains",
      },
    },
    {
      headerName: "Joined Date",
      field: "jdate",
      valueGetter: (p) => {
        const date = p.data?.jdate || p.data?.joinedDate || p.data?.JoinedDate || "";
        return date ? new Date(date).toLocaleDateString() : "N/A";
      },
      minWidth: 130,
      cellRenderer: (p) => (p.value || "N/A"),
      filter: "agTextColumnFilter",
      filterParams: {
        filterOptions: ["contains", "equals", "startsWith", "endsWith"],
        defaultOption: "contains",
      },
    },
  ];


  useEffect(() => {
    if (open) {
      setViewAllMembers(showAllChapters);
      fetchChapters();
      if (showAllChapters) {
        fetchAllChapterMembers();
      }
    }
  }, [open, showAllChapters]);

  useEffect(() => {
    if (selectedChapter && !viewAllMembers) {
      fetchChapterMembers(selectedChapter.chapterId);
      fetchChapterRoles(selectedChapter.chapterId);
    }
  }, [selectedChapter, viewAllMembers]);

  const fetchChapters = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axiosInstance.get(API_ENDPOINTS.ADMIN.GET_CHAPTERS);
      if (response.data) {
        setChapters(response.data);
        if (response.data.length > 0 && !selectedChapter) {
          setSelectedChapter(response.data[0]);
        }
      }
    } catch (err) {
      setError(err.response?.data?.Message || "Failed to fetch chapters");
      console.error("Error fetching chapters:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchChapterMembers = async (chapterId) => {
    try {
      setLoadingMembers(true);
      const response = await axiosInstance.get(
        API_ENDPOINTS.PROFILE.FETCH_CHAPTER_MEMBERS(chapterId)
      );
      if (response.data) {
        setChapterMembers(response.data);
      }
    } catch (err) {
      console.error("Error fetching chapter members:", err);
      setChapterMembers([]);
    } finally {
      setLoadingMembers(false);
    }
  };

  const fetchAllChapterMembers = async () => {
    try {
      setLoadingMembers(true);
      // Fetch all members by passing chapterId = 0
      const response = await axiosInstance.get(
        API_ENDPOINTS.PROFILE.FETCH_CHAPTER_MEMBERS(0)
      );
      if (response.data) {
        setChapterMembers(response.data);
      }
    } catch (err) {
      console.error("Error fetching all chapter members:", err);
      setChapterMembers([]);
    } finally {
      setLoadingMembers(false);
    }
  };

  const handleExportCSV = () => {
    if (gridRef.current && gridRef.current.api) {
      gridRef.current.api.exportDataAsCsv({
        fileName: viewAllMembers ? "all_chapter_members.csv" : `chapter_${selectedChapter?.chapterName || "members"}_members.csv`,
      });
    }
  };

  const fetchChapterRoles = async (chapterId) => {
    try {
      setLoadingRoles(true);
      const response = await axiosInstance.get(
        API_ENDPOINTS.ADMIN.FETCH_CHAPTER_MEMBER_ROLES(chapterId)
      );
      if (response.data) {
        setChapterRoles(response.data);
      }
    } catch (err) {
      console.error("Error fetching chapter roles:", err);
      setChapterRoles([]);
    } finally {
      setLoadingRoles(false);
    }
  };

  const handleChapterSelect = (chapter) => {
    if (chapter === "all") {
      setViewAllMembers(true);
      setSelectedChapter(null);
      fetchAllChapterMembers();
    } else {
      setViewAllMembers(false);
      setSelectedChapter(chapter);
      setActiveTab(0);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleClose = () => {
    setSelectedChapter(null);
    setChapterMembers([]);
    setChapterRoles([]);
    setActiveTab(0);
    setViewAllMembers(false);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          height: "90vh",
          maxHeight: "90vh",
        },
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <GroupWorkIcon color="primary" />
            <Typography variant="h5" fontWeight="bold">
              Chapter Reports
            </Typography>
          </Box>
          <Button onClick={handleClose} color="inherit">
            Close
          </Button>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : (
          <Grid container spacing={3}>
            {/* Left Sidebar - Chapter List */}
            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Typography variant="body2" gutterBottom fontWeight="bold" sx={{ fontSize: "0.75rem" }}>
                    All Chapters ({chapters.length})
                  </Typography>
                  <Box sx={{ maxHeight: "60vh", overflowY: "auto" }}>
                    <Card
                      sx={{
                        mb: 1,
                        cursor: "pointer",
                        border: viewAllMembers ? "2px solid" : "1px solid",
                        borderColor: viewAllMembers ? "primary.main" : "divider",
                        backgroundColor: viewAllMembers ? "primary.lighter" : "inherit",
                        "&:hover": {
                          backgroundColor: "action.hover",
                        },
                      }}
                      onClick={() => handleChapterSelect("all")}
                    >
                      <CardContent sx={{ py: 1, "&:last-child": { pb: 1 } }}>
                        <Typography variant="body2" fontWeight="bold" color="primary" sx={{ fontSize: "0.7rem" }}>
                          All Chapters Members
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.65rem" }}>
                          View all members from all chapters
                        </Typography>
                      </CardContent>
                    </Card>
                    {chapters.map((chapter) => (
                      <Card
                        key={chapter.chapterId}
                        sx={{
                          mb: 1,
                          cursor: "pointer",
                          border:
                            selectedChapter?.chapterId === chapter.chapterId
                              ? "2px solid"
                              : "1px solid",
                          borderColor:
                            selectedChapter?.chapterId === chapter.chapterId
                              ? "primary.main"
                              : "divider",
                          "&:hover": {
                            backgroundColor: "action.hover",
                          },
                        }}
                        onClick={() => handleChapterSelect(chapter)}
                      >
                        <CardContent sx={{ py: 1, "&:last-child": { pb: 1 } }}>
                          <Typography variant="body2" fontWeight="bold" sx={{ fontSize: "0.7rem" }}>
                            {chapter.chapterName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.65rem" }}>
                            {chapter.chapterShortName}
                          </Typography>
                          <Box mt={0.5}>
                            <Chip
                              label={chapter.isActive ? "Active" : "Inactive"}
                              size="small"
                              color={chapter.isActive ? "success" : "default"}
                              sx={{ height: 18, fontSize: "0.65rem" }}
                            />
                          </Box>
                        </CardContent>
                      </Card>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Right Content - Chapter Details */}
            <Grid item xs={12} md={9}>
              {viewAllMembers ? (
                <Card>
                  <CardContent>
                    <Box mb={2} display="flex" justifyContent="space-between" alignItems="center">
                      <Box>
                        <Typography variant="h5" fontWeight="bold" gutterBottom>
                          All Chapters Members
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Total Members: {chapterMembers.length}
                        </Typography>
                      </Box>
                      <Button
                        variant="contained"
                        color="success"
                        startIcon={<DescriptionIcon />}
                        onClick={handleExportCSV}
                        disabled={chapterMembers.length === 0}
                      >
                        Export CSV
                      </Button>
                    </Box>
                    <Divider sx={{ mb: 2 }} />
                    {loadingMembers ? (
                      <Box display="flex" justifyContent="center" p={3}>
                        <CircularProgress />
                      </Box>
                    ) : chapterMembers.length === 0 ? (
                      <Alert severity="info">No members found.</Alert>
                    ) : (
                      <Box className="ag-theme-alpine" sx={{ width: "100%", height: 500, "& .ag-cell": { fontSize: 12 } }}>
                        <AgGridReact
                          ref={gridRef}
                          rowData={chapterMembers}
                          columnDefs={getMemberColumnDefs(true)}
                          domLayout="normal"
                          animateRows
                          suppressCellFocus
                          suppressRowClickSelection
                          headerHeight={48}
                          rowHeight={42}
                          pagination
                          paginationPageSize={20}
                          menuTabs={["filterMenuTab", "generalMenuTab"]}
                          floatingFilter={true}
                          defaultColDef={{
                            sortable: true,
                            filter: true,
                            resizable: true,
                            wrapHeaderText: true,
                            autoHeaderHeight: true,
                            menuTabs: ["filterMenuTab", "generalMenuTab"],
                            floatingFilter: true,
                          }}
                        />
                      </Box>
                    )}
                  </CardContent>
                </Card>
              ) : selectedChapter ? (
                <Card>
                  <CardContent>
                    <Box mb={2}>
                      <Typography variant="h5" fontWeight="bold" gutterBottom>
                        {selectedChapter.chapterName}
                      </Typography>
                      <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                        {selectedChapter.chapterShortName}
                      </Typography>
                    </Box>

                    <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 2 }}>
                      <Tab label="Chapter Details" />
                      <Tab label="Members" />
                      <Tab label="Roles" />
                    </Tabs>

                    <Divider sx={{ mb: 2 }} />

                    {/* Tab Panel 0: Chapter Details */}
                    {activeTab === 0 && (
                      <TableContainer component={Paper} variant="outlined">
                        <Table size="small" sx={{ "& .MuiTableCell-root": { fontSize: "0.75rem", py: 0.5 } }}>
                          <TableBody>
                            <TableRow>
                              <TableCell sx={{ fontWeight: "bold", width: "30%" }}>Chapter Name</TableCell>
                              <TableCell>{selectedChapter.chapterName}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: "bold" }}>Short Name</TableCell>
                              <TableCell>{selectedChapter.chapterShortName || "N/A"}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: "bold" }}>Location</TableCell>
                              <TableCell>{selectedChapter.location || "Not specified"}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: "bold" }}>Status</TableCell>
                              <TableCell>
                                <Chip
                                  label={selectedChapter.isActive ? "Active" : "Inactive"}
                                  size="small"
                                  color={selectedChapter.isActive ? "success" : "default"}
                                  sx={{ height: 20, fontSize: "0.7rem" }}
                                />
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: "bold" }}>Total Members</TableCell>
                              <TableCell>{chapterMembers.length}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: "bold" }}>Created At</TableCell>
                              <TableCell>
                                {selectedChapter.createdAt
                                  ? new Date(selectedChapter.createdAt).toLocaleDateString()
                                  : "N/A"}
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: "bold", verticalAlign: "top" }}>Description</TableCell>
                              <TableCell sx={{ fontSize: "0.7rem" }}>
                                {selectedChapter.description || "No description available"}
                              </TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}

                    {/* Tab Panel 1: Members */}
                    {activeTab === 1 && (
                      <Box>
                        <Box mb={2} display="flex" justifyContent="flex-end">
                          <Button
                            variant="outlined"
                            color="success"
                            size="small"
                            startIcon={<DescriptionIcon />}
                            onClick={handleExportCSV}
                            disabled={chapterMembers.length === 0}
                          >
                            Export CSV
                          </Button>
                        </Box>
                        {loadingMembers ? (
                          <Box display="flex" justifyContent="center" p={3}>
                            <CircularProgress />
                          </Box>
                        ) : chapterMembers.length === 0 ? (
                          <Alert severity="info">No members found for this chapter.</Alert>
                        ) : (
                          <Box className="ag-theme-alpine" sx={{ width: "100%", height: 400, "& .ag-cell": { fontSize: 12 } }}>
                            <AgGridReact
                              ref={gridRef}
                              rowData={chapterMembers}
                              columnDefs={getMemberColumnDefs(false)}
                              domLayout="autoHeight"
                              animateRows
                              suppressCellFocus
                              suppressRowClickSelection
                              headerHeight={48}
                              rowHeight={42}
                              pagination
                              paginationPageSize={10}
                              menuTabs={["filterMenuTab", "generalMenuTab"]}
                              defaultColDef={{
                                sortable: true,
                                filter: true,
                                resizable: true,
                                wrapHeaderText: true,
                                autoHeaderHeight: true,
                                menuTabs: ["filterMenuTab", "generalMenuTab"],
                              }}
                            />
                          </Box>
                        )}
                      </Box>
                    )}

                    {/* Tab Panel 2: Roles */}
                    {activeTab === 2 && (
                      <Box>
                        {loadingRoles ? (
                          <Box display="flex" justifyContent="center" p={3}>
                            <CircularProgress />
                          </Box>
                        ) : chapterRoles.length === 0 ? (
                          <Alert severity="info">No role details found for this chapter.</Alert>
                        ) : (
                          <Box className="ag-theme-alpine" sx={{ width: "100%", height: 350, "& .ag-cell": { fontSize: 12 } }}>
                            <AgGridReact
                              rowData={chapterRoles}
                              columnDefs={roleColumnDefs}
                              domLayout="autoHeight"
                              animateRows
                              suppressCellFocus
                              suppressRowClickSelection
                              headerHeight={48}
                              rowHeight={42}
                              pagination
                              paginationPageSize={10}
                              menuTabs={["filterMenuTab", "generalMenuTab"]}
                              defaultColDef={{
                                sortable: true,
                                filter: true,
                                resizable: true,
                                wrapHeaderText: true,
                                autoHeaderHeight: true,
                                menuTabs: ["filterMenuTab", "generalMenuTab"],
                              }}
                            />
                          </Box>
                        )}
                      </Box>
                    )}
                  </CardContent>
                </Card>
              ) : (
                <Alert severity="info">Please select a chapter or &quot;All Chapters Members&quot; to view details.</Alert>
              )}
            </Grid>
          </Grid>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

AdminChapterReportDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  showAllChapters: PropTypes.bool,
};

export default AdminChapterReportDialog;
